import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Card,
  CardMedia,
  IconButton
} from '@mui/material';
import { CloudUpload, Delete } from '@mui/icons-material';
import {
  dialogFormPaperSx,
  dialogFormScrollContentSx,
  dialogFormTitleSx,
  dialogFormActionsSx,
  uploadDropzoneSx,
  uploadDropzoneCloudIconSx
} from '../utils/dialogFormLayout';
import { buildLive2dModelUrlFromParts } from '../utils/live2dUrls';
import { API_BASE_URL } from '../utils/api';

const LIVE2D_PUBLIC = process.env.PUBLIC_URL || '';
const LIVE2D_FALLBACK_PACKS = ['Lina', 'Mortifera'];

function live2dMotionLabel(file) {
  if (!file || typeof file !== 'string') return '—';
  const base = file.split('/').pop() || file;
  return base.replace(/\.motion3\.json$/i, '');
}

const CharacterForm = ({ open, onClose, onSubmit, character = null, isEdit = false }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [preferenceDominant, setPreferenceDominant] = useState(false);
  const [preferenceSubmissive, setPreferenceSubmissive] = useState(false);
  const [instruction, setInstruction] = useState('');
  const [neutral, setNeutral] = useState('');
  const [trusting, setTrusting] = useState('');
  const [obedient, setObedient] = useState('');
  const [devoted, setDevoted] = useState('');
  const [startFlowId, setStartFlowId] = useState('');
  const [mainFlowId, setMainFlowId] = useState('');
  const [moreFlowId, setMoreFlowId] = useState('');
  const [flows, setFlows] = useState([]);
  const [flowsLoading, setFlowsLoading] = useState(true);

  const [gridPreviewUrl, setGridPreviewUrl] = useState('');
  const [chatPreviewUrl, setChatPreviewUrl] = useState('');
  const [gridFile, setGridFile] = useState(null);
  const [chatFile, setChatFile] = useState(null);
  const [clearAvatarGrid, setClearAvatarGrid] = useState(false);
  const [clearAvatarChat, setClearAvatarChat] = useState(false);
  const gridInputRef = useRef(null);
  const chatInputRef = useRef(null);
  const gridBlobRef = useRef(null);
  const chatBlobRef = useRef(null);

  const [live2dPackList, setLive2dPackList] = useState(LIVE2D_FALLBACK_PACKS);
  const [live2dPack, setLive2dPack] = useState('');
  const [live2dModelPath, setLive2dModelPath] = useState('');
  const [live2dIdleMotionIndex, setLive2dIdleMotionIndex] = useState(0);
  const [live2dIndexData, setLive2dIndexData] = useState(null);
  const [live2dIndexLoading, setLive2dIndexLoading] = useState(false);
  const [live2dIndexErr, setLive2dIndexErr] = useState('');
  const [live2dModelJson, setLive2dModelJson] = useState(null);
  const [live2dModelLoading, setLive2dModelLoading] = useState(false);
  const [live2dModelErr, setLive2dModelErr] = useState('');
  const [animControllers, setAnimControllers] = useState([]);
  const TIER_KEYS = ['neutral', 'trusting', 'obedient', 'devoted'];
  const [titlesByTier, setTitlesByTier] = useState({});
  const [animControllersLoading, setAnimControllersLoading] = useState(false);
  const [live2dAnimControllerId, setLive2dAnimControllerId] = useState('');

  const releaseGridBlob = () => {
    if (gridBlobRef.current) {
      URL.revokeObjectURL(gridBlobRef.current);
      gridBlobRef.current = null;
    }
  };
  const releaseChatBlob = () => {
    if (chatBlobRef.current) {
      URL.revokeObjectURL(chatBlobRef.current);
      chatBlobRef.current = null;
    }
  };

  const fetchFlows = useCallback(async () => {
    setFlowsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/flows/summary`);
      const data = await response.json();
      setFlows(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading flows:', error);
      setFlows([]);
    } finally {
      setFlowsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchFlows();
    }
  }, [open, fetchFlows]);

  useEffect(() => {
    if (!open) return undefined;
    let cancel = false;
    setAnimControllersLoading(true);
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/live2d-anim-controllers`);
        const data = await res.json();
        if (cancel) return;
        setAnimControllers(Array.isArray(data) ? data : []);
      } catch {
        if (!cancel) setAnimControllers([]);
      } finally {
        if (!cancel) setAnimControllersLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    let cancel = false;
    (async () => {
      try {
        const res = await fetch(`${LIVE2D_PUBLIC}/live2d/packs.json`);
        if (!res.ok) throw new Error('packs');
        const data = await res.json();
        if (cancel) return;
        const packs = Array.isArray(data?.packs) ? data.packs : LIVE2D_FALLBACK_PACKS;
        setLive2dPackList(packs.length ? packs : LIVE2D_FALLBACK_PACKS);
      } catch {
        if (!cancel) setLive2dPackList(LIVE2D_FALLBACK_PACKS);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open || !live2dPack) {
      setLive2dIndexData(null);
      setLive2dIndexErr('');
      setLive2dIndexLoading(false);
      return undefined;
    }
    let cancel = false;
    setLive2dIndexLoading(true);
    setLive2dIndexErr('');
    setLive2dIndexData(null);
    (async () => {
      try {
        const res = await fetch(
          `${LIVE2D_PUBLIC}/live2d/${encodeURIComponent(live2dPack)}/index.json`
        );
        if (!res.ok) throw new Error('index');
        const data = await res.json();
        if (cancel) return;
        setLive2dIndexData(data);
      } catch {
        if (!cancel) setLive2dIndexErr('Could not load Live2D index for this pack');
      } finally {
        if (!cancel) setLive2dIndexLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [open, live2dPack]);

  useEffect(() => {
    if (!open || !live2dPack || !live2dModelPath) {
      setLive2dModelJson(null);
      setLive2dModelErr('');
      setLive2dModelLoading(false);
      return undefined;
    }
    const ac = new AbortController();
    setLive2dModelLoading(true);
    setLive2dModelErr('');
    setLive2dModelJson(null);
    const url = buildLive2dModelUrlFromParts(LIVE2D_PUBLIC, live2dPack, live2dModelPath);
    (async () => {
      try {
        const res = await fetch(url, { signal: ac.signal, cache: 'no-store' });
        if (!res.ok) throw new Error('model');
        const data = await res.json();
        if (ac.signal.aborted) return;
        setLive2dModelJson(data);
      } catch {
        if (ac.signal.aborted) return;
        setLive2dModelErr('Could not load model JSON');
      } finally {
        if (!ac.signal.aborted) setLive2dModelLoading(false);
      }
    })();
    return () => ac.abort();
  }, [open, live2dPack, live2dModelPath]);

  const idleMotionsAdmin = live2dModelJson?.FileReferences?.Motions?.idle || [];

  useEffect(() => {
    const n = idleMotionsAdmin.length;
    if (!n) return;
    setLive2dIdleMotionIndex((i) => Math.min(Math.max(0, i), n - 1));
  }, [live2dModelJson, idleMotionsAdmin.length]);

  // Update form fields when character prop changes
  useEffect(() => {
    releaseGridBlob();
    releaseChatBlob();
    if (character) {
      setName(character.name || '');
      setDescription(character.description || '');
      setPreferenceDominant(Boolean(character.preferenceDominant));
      setPreferenceSubmissive(Boolean(character.preferenceSubmissive));
      setInstruction(character.instruction || '');
      setNeutral(character.neutral || '');
      setTrusting(character.trusting || '');
      setObedient(character.obedient || '');
      setDevoted(character.devoted || '');
      setStartFlowId(character.startFlowId || '');
      setMainFlowId(character.mainFlowId || '');
      setMoreFlowId(character.moreFlowId || '');
      setGridPreviewUrl(character.avatarGridUrl || '');
      setChatPreviewUrl(character.avatarChatUrl || '');
      setGridFile(null);
      setChatFile(null);
      setClearAvatarGrid(false);
      setClearAvatarChat(false);
      setLive2dPack(character.live2dPack || '');
      setLive2dModelPath(character.live2dModelPath || '');
      setLive2dIdleMotionIndex(
        Number.isFinite(Number(character.live2dIdleMotionIndex))
          ? Math.max(0, Math.floor(Number(character.live2dIdleMotionIndex)))
          : 0
      );
      const ac = character.live2dAnimControllerId;
      setLive2dAnimControllerId(
        ac && typeof ac === 'object' && ac._id != null ? String(ac._id) : String(ac || '').trim()
      );
      // titlesByTier: array of {tier, titles[]} → object keyed by tier
      const tbt = {};
      (character.titlesByTier || []).forEach((entry) => {
        if (entry.tier) tbt[entry.tier] = (entry.titles || []).join(', ');
      });
      setTitlesByTier(tbt);
    } else {
      setName('');
      setDescription('');
      setPreferenceDominant(false);
      setPreferenceSubmissive(false);
      setInstruction('');
      setNeutral('');
      setTrusting('');
      setObedient('');
      setDevoted('');
      setStartFlowId('');
      setMainFlowId('');
      setMoreFlowId('');
      setGridPreviewUrl('');
      setChatPreviewUrl('');
      setGridFile(null);
      setChatFile(null);
      setClearAvatarGrid(false);
      setClearAvatarChat(false);
      setLive2dPack('');
      setLive2dModelPath('');
      setLive2dIdleMotionIndex(0);
      setLive2dAnimControllerId('');
      setTitlesByTier({});
    }
  }, [character]);

  const handleGridFile = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    releaseGridBlob();
    const url = URL.createObjectURL(file);
    gridBlobRef.current = url;
    setGridPreviewUrl(url);
    setGridFile(file);
    setClearAvatarGrid(false);
  };

  const handleChatFile = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    releaseChatBlob();
    const url = URL.createObjectURL(file);
    chatBlobRef.current = url;
    setChatPreviewUrl(url);
    setChatFile(file);
    setClearAvatarChat(false);
  };

  const handleRemoveGrid = () => {
    if (gridFile) {
      releaseGridBlob();
      setGridFile(null);
      setGridPreviewUrl(character?.avatarGridUrl || '');
      setClearAvatarGrid(false);
      return;
    }
    releaseGridBlob();
    setGridPreviewUrl('');
    setClearAvatarGrid(Boolean(character?.avatarGridUrl));
  };

  const handleRemoveChat = () => {
    if (chatFile) {
      releaseChatBlob();
      setChatFile(null);
      setChatPreviewUrl(character?.avatarChatUrl || '');
      setClearAvatarChat(false);
      return;
    }
    releaseChatBlob();
    setChatPreviewUrl('');
    setClearAvatarChat(Boolean(character?.avatarChatUrl));
  };

  const resetAvatarFields = () => {
    releaseGridBlob();
    releaseChatBlob();
    setGridPreviewUrl('');
    setChatPreviewUrl('');
    setGridFile(null);
    setChatFile(null);
    setClearAvatarGrid(false);
    setClearAvatarChat(false);
    if (gridInputRef.current) gridInputRef.current.value = '';
    if (chatInputRef.current) chatInputRef.current.value = '';
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (name.trim()) {
      onSubmit({
        name: name.trim(),
        description: description.trim(),
        preferenceDominant,
        preferenceSubmissive,
        instruction: instruction.trim(),
        neutral: neutral.trim(),
        trusting: trusting.trim(),
        obedient: obedient.trim(),
        devoted: devoted.trim(),
        startFlowId: startFlowId || null,
        mainFlowId: mainFlowId || null,
        moreFlowId: moreFlowId || null,
        avatarGridFile: gridFile,
        avatarChatFile: chatFile,
        clearAvatarGrid,
        clearAvatarChat,
        live2dPack: live2dPack.trim() ? live2dPack.trim() : '',
        live2dModelPath:
          live2dPack.trim() && live2dModelPath.trim() ? live2dModelPath.trim() : '',
        live2dIdleMotionIndex: Math.max(0, Math.floor(Number(live2dIdleMotionIndex) || 0)),
        live2dAnimControllerId: live2dAnimControllerId.trim() || null,
        titlesByTier: TIER_KEYS
          .filter((tier) => (titlesByTier[tier] || '').trim())
          .map((tier) => ({
            tier,
            titles: (titlesByTier[tier] || '').split(',').map((s) => s.trim()).filter(Boolean)
          }))
      });
      setName('');
      setDescription('');
      setPreferenceDominant(false);
      setPreferenceSubmissive(false);
      setInstruction('');
      setNeutral('');
      setTrusting('');
      setObedient('');
      setDevoted('');
      setStartFlowId('');
      setMainFlowId('');
      setMoreFlowId('');
      setLive2dPack('');
      setLive2dModelPath('');
      setLive2dIdleMotionIndex(0);
      setLive2dAnimControllerId('');
      setTitlesByTier({});
      resetAvatarFields();
      onClose();
    }
  };

  const handleClose = () => {
    setName('');
    setDescription('');
    setPreferenceDominant(false);
    setPreferenceSubmissive(false);
    setInstruction('');
    setNeutral('');
    setTrusting('');
    setObedient('');
    setDevoted('');
    setStartFlowId('');
    setMainFlowId('');
    setMoreFlowId('');
    setLive2dPack('');
    setLive2dModelPath('');
    setLive2dIdleMotionIndex(0);
    setLive2dAnimControllerId('');
    resetAvatarFields();
    onClose();
  };

  const flowExists = (flowId) =>
    !!flowId && flows.some((f) => String(f._id) === String(flowId));

  /** Empty while flow list loads — avoids MUI Select value out of range */
  const flowSelectDisplayedValue = (flowId) =>
    flowsLoading ? '' : flowId ? String(flowId) : '';

  const renderOrphanFlowOption = (flowId) =>
    !flowsLoading && flowId && !flowExists(flowId) ? (
      <MenuItem key={`orphan-${flowId}`} value={String(flowId)} disabled sx={{ fontStyle: 'italic' }}>
        Missing flow (removed from list)
      </MenuItem>
    ) : null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: dialogFormPaperSx }}
    >
      <DialogTitle sx={dialogFormTitleSx}>
        {isEdit ? 'Edit Character' : 'Create New Character'}
      </DialogTitle>
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: '1 1 auto',
          minHeight: 0,
          overflow: 'hidden'
        }}
      >
        <DialogContent sx={(theme) => dialogFormScrollContentSx(theme)}>
          <TextField
            autoFocus
            margin="dense"
            label="Character Name"
            type="text"
            fullWidth
            variant="outlined"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Description (player-facing)"
            multiline
            minRows={3}
            maxRows={12}
            fullWidth
            variant="outlined"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Short profile text shown in the game lobby / character window…"
            sx={{ mb: 2 }}
          />

          <Typography variant="subtitle1" sx={{ mt: 1, mb: 1, fontWeight: 600 }}>
            Lobby and chat images
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Large image for the character grid; small avatar in the chat contact list.
          </Typography>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Grid portrait (lobby cards)
            </Typography>
            {gridPreviewUrl ? (
              <Card sx={{ mb: 1, maxWidth: 280 }}>
                <CardMedia
                  component="img"
                  height="200"
                  image={gridPreviewUrl}
                  alt=""
                  sx={{ objectFit: 'cover' }}
                />
                <Box sx={{ p: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: '70%' }}>
                    {gridFile ? gridFile.name : 'Current image'}
                  </Typography>
                  <IconButton size="small" onClick={handleRemoveGrid} color="error" aria-label="Remove grid image">
                    <Delete />
                  </IconButton>
                </Box>
              </Card>
            ) : (
              <Box sx={(theme) => uploadDropzoneSx(theme)} onClick={() => gridInputRef.current?.click()}>
                <CloudUpload sx={uploadDropzoneCloudIconSx} />
                <Typography variant="body2" color="text.secondary">
                  Click to upload grid image
                </Typography>
              </Box>
            )}
            <input
              ref={gridInputRef}
              type="file"
              accept="image/*"
              onChange={handleGridFile}
              style={{ display: 'none' }}
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Chat thumbnail
            </Typography>
            {chatPreviewUrl ? (
              <Card sx={{ mb: 1, maxWidth: 200 }}>
                <CardMedia
                  component="img"
                  height="120"
                  image={chatPreviewUrl}
                  alt=""
                  sx={{ objectFit: 'cover' }}
                />
                <Box sx={{ p: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: '65%' }}>
                    {chatFile ? chatFile.name : 'Current image'}
                  </Typography>
                  <IconButton size="small" onClick={handleRemoveChat} color="error" aria-label="Remove chat image">
                    <Delete />
                  </IconButton>
                </Box>
              </Card>
            ) : (
              <Box sx={(theme) => uploadDropzoneSx(theme)} onClick={() => chatInputRef.current?.click()}>
                <CloudUpload sx={uploadDropzoneCloudIconSx} />
                <Typography variant="body2" color="text.secondary">
                  Click to upload chat thumbnail
                </Typography>
              </Box>
            )}
            <input
              ref={chatInputRef}
              type="file"
              accept="image/*"
              onChange={handleChatFile}
              style={{ display: 'none' }}
            />
          </Box>

          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
            Live2D (character window)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Pack from <code>public/live2d/packs.json</code>, then model from that pack&apos;s{' '}
            <code>index.json</code>, then a clip from <code>FileReferences.Motions.idle</code> in{' '}
            <strong>that</strong> <code>.model3.json</code> only. A long list is normal:{' '}
            <code>npm run live2d:sync-motions</code> fills <code>idle</code> with every{' '}
            <code>.motion3.json</code> under the model folder (recursive).
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Live2D pack</InputLabel>
            <Select
              value={live2dPack}
              label="Live2D pack"
              onChange={(e) => {
                const v = e.target.value;
                setLive2dPack(v);
                setLive2dModelPath('');
                setLive2dIdleMotionIndex(0);
              }}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {live2dPackList.map((p) => (
                <MenuItem key={p} value={p}>
                  {p}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }} disabled={!live2dPack || live2dIndexLoading}>
            <InputLabel>Model (.model3.json)</InputLabel>
            <Select
              value={live2dModelPath}
              label="Model (.model3.json)"
              onChange={(e) => {
                setLive2dModelPath(e.target.value);
                setLive2dIdleMotionIndex(0);
              }}
            >
              <MenuItem value="">
                <em>Select model</em>
              </MenuItem>
              {live2dIndexErr ? (
                <MenuItem value="" disabled>
                  {live2dIndexErr}
                </MenuItem>
              ) : null}
              {(live2dIndexData?.models || []).map((m) => (
                <MenuItem key={m.path} value={m.path}>
                  {m.path}
                </MenuItem>
              ))}
              {live2dModelPath &&
              live2dIndexData?.models &&
              !live2dIndexData.models.some((m) => m.path === live2dModelPath) ? (
                <MenuItem value={live2dModelPath} disabled sx={{ fontStyle: 'italic' }}>
                  Missing in index: {live2dModelPath}
                </MenuItem>
              ) : null}
            </Select>
          </FormControl>
          <FormControl
            key={`live2d-idle-${live2dPack}-${live2dModelPath}`}
            fullWidth
            sx={{ mb: 2 }}
            disabled={!live2dModelPath || live2dModelLoading || !idleMotionsAdmin.length}
          >
            <InputLabel>Idle motion</InputLabel>
            <Select
              value={idleMotionsAdmin.length ? String(live2dIdleMotionIndex) : ''}
              label="Idle motion"
              onChange={(e) => setLive2dIdleMotionIndex(Number(e.target.value) || 0)}
              renderValue={(selected) => {
                if (selected === '' || !idleMotionsAdmin.length) return '';
                const idx = Number(selected);
                const entry = idleMotionsAdmin[idx];
                if (!entry) return String(selected);
                const file = entry.File || entry.file;
                return `${idx}. ${live2dMotionLabel(file)}`;
              }}
            >
              {!live2dModelPath ? (
                <MenuItem value="" disabled>
                  Select a model first
                </MenuItem>
              ) : live2dModelLoading ? (
                <MenuItem value="" disabled>
                  Loading motions…
                </MenuItem>
              ) : live2dModelErr ? (
                <MenuItem value="" disabled>
                  {live2dModelErr}
                </MenuItem>
              ) : !idleMotionsAdmin.length ? (
                <MenuItem value="" disabled>
                  No idle motions in this model
                </MenuItem>
              ) : (
                idleMotionsAdmin.map((entry, i) => {
                  const file = entry?.File || entry?.file;
                  const line = `${i}. ${live2dMotionLabel(file)}`;
                  return (
                    <MenuItem key={`${file}-${i}`} value={String(i)} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {line}
                    </MenuItem>
                  );
                })
              )}
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }} disabled={animControllersLoading}>
            <InputLabel>Live2D animation graph (optional)</InputLabel>
            <Select
              value={live2dAnimControllerId}
              label="Live2D animation graph (optional)"
              onChange={(e) => setLive2dAnimControllerId(e.target.value)}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {animControllers.map((row) => (
                <MenuItem key={row._id} value={String(row._id)}>
                  {row.name || row._id} ({row.live2dPack || '—'})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Preferences
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={preferenceDominant}
                  onChange={(e) => setPreferenceDominant(e.target.checked)}
                />
              }
              label="Dominant"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={preferenceSubmissive}
                  onChange={(e) => setPreferenceSubmissive(e.target.checked)}
                />
              }
              label="Submissive"
            />
          </Box>
          {preferenceDominant ? (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Titles by Tier (comma-separated, for Submission Contract)
              </Typography>
              {TIER_KEYS.map((tier) => (
                <TextField
                  key={tier}
                  margin="dense"
                  label={tier.charAt(0).toUpperCase() + tier.slice(1) + ' titles'}
                  fullWidth
                  variant="outlined"
                  placeholder="e.g. Plaything, Pet, Toy"
                  value={titlesByTier[tier] || ''}
                  onChange={(e) => setTitlesByTier((prev) => ({ ...prev, [tier]: e.target.value }))}
                />
              ))}
            </Box>
          ) : null}
          <TextField
            margin="dense"
            label="Instruction"
            multiline
            minRows={8}
            maxRows={48}
            fullWidth
            variant="outlined"
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
            placeholder="Enter the instruction text for this character..."
            sx={{ mb: 2 }}
          />

          <Typography variant="subtitle1" sx={{ mt: 1, mb: 1.5, fontWeight: 600 }}>
            Relationship instructions
          </Typography>

          <TextField
            margin="dense"
            label="Neutral"
            multiline
            minRows={6}
            maxRows={32}
            fullWidth
            variant="outlined"
            value={neutral}
            onChange={(e) => setNeutral(e.target.value)}
            placeholder="Enter neutral relationship instruction..."
            sx={{ mb: 2 }}
          />

          <TextField
            margin="dense"
            label="Trusting"
            multiline
            minRows={6}
            maxRows={32}
            fullWidth
            variant="outlined"
            value={trusting}
            onChange={(e) => setTrusting(e.target.value)}
            placeholder="Enter trusting relationship instruction..."
            sx={{ mb: 2 }}
          />

          <TextField
            margin="dense"
            label="Obedient"
            multiline
            minRows={6}
            maxRows={32}
            fullWidth
            variant="outlined"
            value={obedient}
            onChange={(e) => setObedient(e.target.value)}
            placeholder="Enter obedient relationship instruction..."
            sx={{ mb: 2 }}
          />

          <TextField
            margin="dense"
            label="Devoted"
            multiline
            minRows={6}
            maxRows={32}
            fullWidth
            variant="outlined"
            value={devoted}
            onChange={(e) => setDevoted(e.target.value)}
            placeholder="Enter devoted relationship instruction..."
            sx={{ mb: 2 }}
          />

          <Typography variant="subtitle1" sx={{ mt: 1, mb: 1.5, fontWeight: 600 }}>
            Flow assignments
          </Typography>

          {flowsLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">
                Loading flow list…
              </Typography>
            </Box>
          )}

          <FormControl fullWidth sx={{ mb: 2 }} disabled={flowsLoading}>
            <InputLabel>Start Flow</InputLabel>
            <Select
              value={flowSelectDisplayedValue(startFlowId)}
              onChange={(e) => setStartFlowId(e.target.value)}
              label="Start Flow"
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {flows.map((flow) => (
                <MenuItem key={flow._id} value={String(flow._id)}>
                  {flow.name}
                </MenuItem>
              ))}
              {renderOrphanFlowOption(startFlowId)}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2 }} disabled={flowsLoading}>
            <InputLabel>Main Flow</InputLabel>
            <Select
              value={flowSelectDisplayedValue(mainFlowId)}
              onChange={(e) => setMainFlowId(e.target.value)}
              label="Main Flow"
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {flows.map((flow) => (
                <MenuItem key={flow._id} value={String(flow._id)}>
                  {flow.name}
                </MenuItem>
              ))}
              {renderOrphanFlowOption(mainFlowId)}
            </Select>
          </FormControl>

          <FormControl fullWidth disabled={flowsLoading} sx={{ mb: 0.5 }}>
            <InputLabel>More Flow</InputLabel>
            <Select
              value={flowSelectDisplayedValue(moreFlowId)}
              onChange={(e) => setMoreFlowId(e.target.value)}
              label="More Flow"
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {flows.map((flow) => (
                <MenuItem key={flow._id} value={String(flow._id)}>
                  {flow.name}
                </MenuItem>
              ))}
              {renderOrphanFlowOption(moreFlowId)}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={dialogFormActionsSx}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button type="submit" variant="contained">
            {isEdit ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default CharacterForm; 